import { _parseChild, Clip, Bin, Sequence, Track } from './model'
import { Time, Framerate } from '../media/time'
import { APIService } from 'api/module'
import { MediaRootDetail } from 'elements-sdk/src'

export class MediaProjectContainer {
  /* model */
  path_format: string
  name: string
  format: string
  is_elements: boolean
  children: (Clip|Bin|Sequence)[]
  /* end model */

  private assetMap = {}
  private fetchingAssets = false
  private __lastID = 0

  constructor (private api: APIService, private root: MediaRootDetail, json: any) {
    this.root = root

    Object.assign(this, json)
    this.children = json.children.map(_parseChild).filter(x => !!x)
    return this
  }

  serialize () {
    return {
      path_format: this.path_format,
      name: this.name,
      format: this.format,
      is_elements: this.is_elements,
      children: this.children.map(x => x.serialize()),
    }
  }

  newID () {
    this.__lastID += 1
    return this.__lastID + ''
  }

  async addSubclip (clipboardEntry) {
    const removeExtension = name => {
      if (name.includes('.')) {
        name = name.substring(0, name.lastIndexOf('.'))
      }
      return name
    }

    const asset = this.assetMap[clipboardEntry.cut.asset.id] ?? await this.api.mediaLibrary.getAsset({ id: clipboardEntry.cut.asset.id, includeProxies: true })
    this.assetMap[clipboardEntry.cut.asset.id] = asset
    clipboardEntry.cut.name ||= asset.displayName

    let clipItem = this.itemForAsset(asset)
    if (!clipItem) {
      const targetBin = (this.children.filter(x => x instanceof Bin) as Bin[]).find(x => x.name === 'Sources') ?? this
      clipItem = new Clip()
      Object.assign(clipItem, {
        id: this.newID(),
        name: removeExtension(asset.displayName),
        is_master: true,
        is_subclip: false,
        master_id: null,
        source_asset_id: asset.id,
        source_bundle_id: clipboardEntry.bundle?.id,
        duration: new Time(asset.info.duration ?? 5),
        timebase: new Framerate(asset.info.framerate ?? 25),
        tIn: Time.zero,
        tOut: new Time(asset.info.duration ?? 5),
      })

      if (asset.type === 'image') {
        clipItem.tIn = new Time(3600)
        clipItem.tOut = new Time(3605)
        clipItem.duration = new Time(7200)
      }
      targetBin.children.push(clipItem)
    }

    if (clipboardEntry.cut.tIn !== 0 || clipboardEntry.cut.tOut !== asset.info.duration) {
      const targetBin = (this.children.filter(x => x instanceof Bin) as Bin[]).find(x => x.name === 'Subclips') ?? this
      const cutItem = new Clip()
      Object.assign(cutItem, {
        id: this.newID(),
        name: removeExtension(clipboardEntry.cut.name),
        is_subclip: true,
        is_master: true,
        master_id: clipItem.id,
        source_asset_id: asset.id,
        source_bundle_id: clipboardEntry.bundle?.id,
        duration: new Time(asset.info?.duration ?? 5),
        timebase: new Framerate(asset.info.framerate ?? 25),
        tIn: new Time(clipboardEntry.cut.tIn),
        tOut: new Time(clipboardEntry.cut.tOut),
      })
      targetBin.children.push(cutItem)
      return cutItem
    }
    return clipItem
  }

  masterClipForItem (clip) {
    if (!clip) {
      return null
    }
    if (clip.is_master) {
      return clip
    }
    return this.masterClipForItem(this.find(x => x instanceof Clip && x.id === clip.master_id))
  }

  removeItem (item) {
    const binRemoveQueue: [Bin, any][] = []
    const seqRemoveQueue: [Track, any][] = []

    const dependsOnItem = (node) => {
      return (
        node === item ||
        [node.id, node.master_id].includes(item.id) ||
        (!item.is_subclip && node.source_asset_id &&
          item.source_asset_id === node.source_asset_id) // Delete subclips
      )
    }

    const scan = (parent: Bin|Sequence|Clip|this) => {
      if (parent instanceof Bin || parent instanceof MediaProjectContainer) {
        for (const child of parent.children.slice()) {
          if (dependsOnItem(child)) {
            binRemoveQueue.push([parent, child])
          }

          scan(child)
        }
      } else if (parent instanceof Sequence) {
        for (const track of parent.tracks.slice()) {
          for (const trackItem of track.items) {
            if (dependsOnItem(trackItem)) {
              seqRemoveQueue.unshift([track, trackItem])
            }
          }
        }
      }
    }

    scan(this)
    for (const [bin, child] of binRemoveQueue) {
      bin.children = bin.children.filter(x => x !== child)
    }
    for (const [track, trackItem] of seqRemoveQueue) {
      track.items = track.items.filter(x => x !== trackItem)
    }
  }

  find (predicate) {
    const scan = (parent) => {
      if (parent.children) { // Bin
        for (const child of parent.children) {
          const x = scan(child)
          if (x) {
            return x
          }
          if (predicate(child)) {
            return child
          }
        }
      }
    }
    return scan(this)
  }

  itemForAsset (asset) {
    return this.find(x => x instanceof Clip && x.source_asset_id === asset.id)
  }

  assetForItem (item) {
    if (!item) {
      return null
    }
    if (!this.masterClipForItem(item)) {
      return null
    }
    return this.assetMap[this.masterClipForItem(item).source_asset_id]
  }

  async fetchAssets () {
    const assetsToFetch: number[] = []
    this.fetchingAssets = true

    const scan = (item: Clip|Sequence|Bin|this) => {
      if (item instanceof Clip) {
        const assetId = item.source_asset_id
        if (assetId) {
          if (!this.assetMap[assetId] && !assetsToFetch.includes(assetId)) {
            assetsToFetch.push(assetId)
          }
        }
      }
      if (item instanceof Bin || item instanceof MediaProjectContainer) {
        for (const child of item.children) {
          scan(child)
        }
      }
    }

    scan(this)

    if (assetsToFetch.length === 0) {
      this.fetchingAssets = false
      return
    }

    const assets = await this.api.mediaLibrary.getMultipleAssets({
      multipleAssetsRequest: { assets: assetsToFetch },
      includeProxies: true,
    })
    for (const asset of assets) {
      this.assetMap[asset.id] = asset
    }
    this.fetchingAssets = false
    console.log(`Fetched ${assets.length} assets`)
  }
}
